import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App.tsx';
import './index.css';

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
);

/**
 * Cursor comet-trail (brand §4 — the motion signature). Ported verbatim from the design system's
 * initSpotlight: a 9-point trail chases the pointer and builds a radial-gradient mask over the
 * `.spotlight` grid overlay so the grid "charges" near the cursor. The rAF loop pauses itself when
 * idle. Mount-once at the app entry. Gated to fine-pointer + no-reduced-motion (the CSS also hides
 * `.spotlight` under reduced-motion); on touch/coarse devices it never runs.
 */
(function initSpotlight() {
  try {
    if (!window.matchMedia) return;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const el = document.querySelector<HTMLElement>('.spotlight');
    if (!el) return;
    const N = 9,
      EASE = 0.32,
      HEAD = 150,
      TAIL = 46;
    const pts: { x: number; y: number }[] = [];
    let target: { x: number; y: number } | null = null;
    let raf = 0;
    function buildMask() {
      let m = '';
      for (let i = 0; i < pts.length; i++) {
        const r = HEAD - ((HEAD - TAIL) * i) / (N - 1);
        if (i) m += ',';
        m +=
          'radial-gradient(circle ' +
          r +
          'px at ' +
          pts[i].x +
          'px ' +
          pts[i].y +
          'px, #000 0%, #000 22%, rgba(0,0,0,0.55) 50%, transparent 74%)';
      }
      el!.style.webkitMaskImage = m;
      el!.style.maskImage = m;
    }
    function tick() {
      raf = 0;
      if (!target) return;
      pts[0].x += (target.x - pts[0].x) * EASE;
      pts[0].y += (target.y - pts[0].y) * EASE;
      for (let i = 1; i < N; i++) {
        pts[i].x += (pts[i - 1].x - pts[i].x) * EASE;
        pts[i].y += (pts[i - 1].y - pts[i].y) * EASE;
      }
      buildMask();
      let moving = false;
      for (let j = 0; j < N; j++) {
        if (Math.abs(pts[j].x - target.x) > 0.6 || Math.abs(pts[j].y - target.y) > 0.6) {
          moving = true;
          break;
        }
      }
      if (moving) raf = requestAnimationFrame(tick);
    }
    document.addEventListener(
      'mousemove',
      (e) => {
        target = { x: e.clientX, y: e.clientY };
        if (!pts.length) {
          for (let i = 0; i < N; i++) pts.push({ x: e.clientX, y: e.clientY });
        }
        el!.style.opacity = '1';
        if (!raf) raf = requestAnimationFrame(tick);
      },
      { passive: true },
    );
    document.addEventListener('mouseleave', () => {
      el!.style.opacity = '0';
    });
  } catch {
    /* no-op: the comet-trail is pure enhancement */
  }
})();
